import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { AlertTriangle, Send, Loader2, Bug, Image, X, Clipboard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from "../config/api";


const ReportProblemButton = ({ 
  pageName = 'Unknown Page',
  variant = 'ghost',
  size = 'sm',
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    expected_behavior: '',
    steps_to_reproduce: '',
  });
  
  // Screenshot state
  const [screenshot, setScreenshot] = useState(null); // { file: File, preview: base64 string }
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const dropZoneRef = useRef(null);

  const token = localStorage.getItem('token');
  const userEmail = localStorage.getItem('userEmail') || 'anonymous';

  // Handle paste from clipboard (PrintScreen + Ctrl+V)
  const handlePaste = useCallback((e) => {
    if (!open) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          processImageFile(file);
          e.preventDefault();
          break;
        }
      }
    }
  }, [open]);

  // Process image file (from paste or drag)
  const processImageFile = (file) => {
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Screenshot too large. Max size is 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setScreenshot({
        file: file,
        preview: e.target.result,
        name: file.name || 'screenshot.png'
      });
      toast.success('Screenshot added!');
    };
    reader.readAsDataURL(file);
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        processImageFile(file);
      } else {
        toast.error('Please drop an image file');
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Remove screenshot
  const removeScreenshot = () => {
    setScreenshot(null);
  };

  // Add paste listener when dialog is open
  useEffect(() => {
    if (open) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [open, handlePaste]);

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error('Please describe the problem');
      return;
    }

    setSubmitting(true);
    try {
      // Get current URL and browser info
      const currentUrl = window.location.href;
      const browserInfo = navigator.userAgent;
      const timestamp = new Date().toISOString();

      let screenshotUrl = null;

      // Upload screenshot if present
      if (screenshot?.file) {
        setUploadingScreenshot(true);
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', screenshot.file);
          formDataUpload.append('folder', 'problem-reports');
          
          const uploadResponse = await axios.post(
            `${API_URL}/upload-file`,
            formDataUpload,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              }
            }
          );
          
          screenshotUrl = uploadResponse.data.url || uploadResponse.data.file_url;
        } catch (uploadError) {
          console.error('Screenshot upload failed:', uploadError);
          // Continue without screenshot
        }
        setUploadingScreenshot(false);
      }

      // Create the problem report
      const reportData = {
        page_name: pageName,
        current_url: currentUrl,
        description: formData.description,
        expected_behavior: formData.expected_behavior,
        steps_to_reproduce: formData.steps_to_reproduce,
        screenshot_url: screenshotUrl,
        screenshot_base64: screenshot?.preview || null, // Also send base64 as fallback
        browser_info: browserInfo,
        reported_by: userEmail,
        timestamp: timestamp
      };

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API_URL}/report-problem`, reportData, { headers });

      toast.success('Problem reported successfully. Our team will look into it.');
      setOpen(false);
      setFormData({
        description: '',
        expected_behavior: '',
        steps_to_reproduce: '',
      });
      setScreenshot(null);
    } catch (error) {
      console.error('Failed to report problem:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadingScreenshot(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={`text-orange-600 hover:text-orange-700 hover:bg-orange-50 ${className}`}
        data-testid="report-problem-btn"
      >
        <Bug className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">Report Problem</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Report a Problem
            </DialogTitle>
            <DialogDescription>
              Help us improve by reporting issues you encounter. Your feedback goes directly to our team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
              <p className="text-orange-800">
                <strong>Page:</strong> {pageName}
              </p>
              <p className="text-orange-600 text-xs mt-1 truncate">
                {window.location.href}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Problem Description *</Label>
              <Textarea
                id="description"
                placeholder="What went wrong? Please describe the issue you encountered..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="problem-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expected">Expected Behavior</Label>
              <Textarea
                id="expected"
                placeholder="What did you expect to happen?"
                value={formData.expected_behavior}
                onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="steps">Steps to Reproduce</Label>
              <Textarea
                id="steps"
                placeholder="1. Click on...&#10;2. Enter...&#10;3. See error..."
                value={formData.steps_to_reproduce}
                onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                rows={3}
              />
            </div>

            {/* Screenshot Paste/Drop Zone */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Screenshot
              </Label>
              
              {screenshot ? (
                // Show preview of pasted screenshot
                <div className="relative rounded-lg border-2 border-green-300 bg-green-50 p-2">
                  <img 
                    src={screenshot.preview} 
                    alt="Screenshot preview" 
                    className="max-h-48 w-full object-contain rounded"
                  />
                  <button
                    onClick={removeScreenshot}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-green-700 mt-2 text-center">
                    Screenshot attached ✓
                  </p>
                </div>
              ) : (
                // Drop zone for paste/drag
                <div
                  ref={dropZoneRef}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400 hover:bg-orange-50/50 transition-colors cursor-pointer"
                  onClick={() => {
                    // Try to read from clipboard on click (for browsers that support it)
                    navigator.clipboard?.read?.().then(items => {
                      for (const item of items) {
                        if (item.types.includes('image/png')) {
                          item.getType('image/png').then(blob => {
                            processImageFile(new File([blob], 'screenshot.png', { type: 'image/png' }));
                          });
                          break;
                        }
                      }
                    }).catch(() => {});
                  }}
                >
                  <Clipboard className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600">
                    Press <kbd className="px-1.5 py-0.5 text-xs bg-slate-200 rounded">PrintScreen</kbd> then <kbd className="px-1.5 py-0.5 text-xs bg-slate-200 rounded">Ctrl+V</kbd> to paste
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    or drag & drop an image here
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || uploadingScreenshot}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-problem-btn"
            >
              {(submitting || uploadingScreenshot) ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {uploadingScreenshot ? 'Uploading...' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportProblemButton;
